import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { listProjects } from './api';
import type { ScopeState } from './types';

const STORAGE_KEY = 'selfplay-scope';

const EMPTY_SCOPE: ScopeState = { project_id: null };

interface ScopeContextValue {
  scope: ScopeState;
  setScope: (scope: ScopeState) => void;
  projectIds: string[];
  refreshProjects: () => void;
}

const ScopeContext = createContext<ScopeContextValue>({
  scope: EMPTY_SCOPE,
  setScope: () => undefined,
  projectIds: [],
  refreshProjects: () => undefined,
});

function loadScope(): ScopeState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SCOPE;
    const parsed = JSON.parse(raw) as Partial<ScopeState>;
    return { project_id: parsed.project_id ?? null };
  } catch {
    return EMPTY_SCOPE;
  }
}

export const ScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scope, setScopeState] = useState<ScopeState>(loadScope);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    listProjects().then(setProjectIds).catch(() => undefined);
  }, [refreshTick]);

  const setScope = useCallback((next: ScopeState) => {
    setScopeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage may be unavailable (private mode); scope stays in memory
    }
  }, []);

  const refreshProjects = useCallback(() => setRefreshTick((t) => t + 1), []);

  return (
    <ScopeContext.Provider value={{ scope, setScope, projectIds, refreshProjects }}>
      {children}
    </ScopeContext.Provider>
  );
};

export function useScope(): ScopeContextValue {
  return useContext(ScopeContext);
}
